import { useState, useRef, useEffect } from 'react';
import { useCalendarDataContext } from '../contexts/CalendarDataContext';
import { useTranslation } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import { shares } from '../api';
import LoginButton from './LoginButton';
import CalendarSwitcher from './CalendarSwitcher';
import SyncIndicator from './SyncIndicator';
import './Header.scss';

/**
 * Application header with year navigation, project management, and actions.
 * Displays project pills that can be dragged to reorder, clicked to select,
 * and toggled for visibility.
 * 
 * @param {Object} props
 * @param {number} props.year - Currently displayed year
 * @param {Function} props.onPrevYear - Handler for previous year button
 * @param {Function} props.onNextYear - Handler for next year button
 * @param {Function} props.onGoToCurrentYear - Handler for clicking year label
 * @param {Function} props.onAddProject - Handler for add project button
 * @param {Function} props.onEditProject - Handler for edit project (project) => void
 * @param {Function} props.onOpenSettings - Handler for settings button
 * @param {Function} props.onShowInfo - Handler for info button
 */
function Header({ 
  year, 
  onPrevYear, 
  onNextYear, 
  onGoToCurrentYear,
  onAddProject,
  onEditProject,
  onOpenSettings,
  onShowInfo,
  onManageCalendars,
  onShowSharing
}) {
  // Get projects and handlers from context
  const { 
    projects,
    selectedProjectId,
    selectProject: onSelectProject,
    toggleProjectVisibility: onToggleVisibility,
    reorderProjects: onReorderProjects
  } = useCalendarDataContext();

  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [pendingSharesCount, setPendingSharesCount] = useState(0);

  // Fetch pending shares count (shared projects not yet included in calendar)
  useEffect(() => {
    if (!isAuthenticated || !onShowSharing) return;

    async function fetchPendingShares() {
      try {
        const sharedProjects = await shares.getSharedWithMe();
        // Count projects that aren't included in the calendar yet
        const pendingCount = sharedProjects.filter(p => !p.isIncluded).length;
        setPendingSharesCount(pendingCount);
      } catch (err) {
        // Silently fail - badge just won't show
        console.error('Failed to fetch pending shares:', err);
      }
    }

    fetchPendingShares();
    // Refresh periodically
    const interval = setInterval(fetchPendingShares, 30000);

    // Listen for share changes via WebSocket
    function handleShareChanged() {
      fetchPendingShares();
    }
    window.addEventListener('lincal:share-changed', handleShareChanged);

    return () => {
      clearInterval(interval);
      window.removeEventListener('lincal:share-changed', handleShareChanged);
    };
  }, [isAuthenticated, onShowSharing]);
  const projectsContainerRef = useRef(null);
  const dragStateRef = useRef({
    itemCenters: [],
    currentDropIndex: null,
  });

  // Mouse-based drag reordering
  const handleMouseDown = (e, index) => {
    // Only left mouse button, and not on utility buttons
    if (e.button !== 0 || e.target.closest('button:not(.header__project-pill)')) return;
    
    e.preventDefault();
    
    // Cache item centers at drag start
    const container = projectsContainerRef.current;
    if (container) {
      const wrappers = container.querySelectorAll('.header__project-wrapper');
      dragStateRef.current.itemCenters = Array.from(wrappers).map(el => {
        const rect = el.getBoundingClientRect();
        return (rect.left + rect.right) / 2;
      });
    }
    
    dragStateRef.current.currentDropIndex = null;
    setDraggedIndex(index);
    setDropIndex(null);
  };

  useEffect(() => {
    if (draggedIndex === null) return;

    const handleMouseMove = (e) => {
      const { itemCenters, currentDropIndex } = dragStateRef.current;
      if (!itemCenters.length) return;
      
      const clientX = e.clientX;
      const numItems = itemCenters.length;
      
      // Calculate raw target position
      let targetPos = numItems; // Default: after last item
      for (let i = 0; i < numItems; i++) {
        if (clientX < itemCenters[i]) {
          targetPos = i;
          break;
        }
      }
      
      // Convert to drop index (where item will be after removal and reinsertion)
      let newDropIndex;
      if (targetPos <= draggedIndex) {
        newDropIndex = targetPos;
      } else if (targetPos > draggedIndex + 1) {
        newDropIndex = targetPos - 1;
      } else {
        // targetPos is draggedIndex + 1, meaning cursor is between dragged item and next
        // This is effectively "no move" - keep at original position
        newDropIndex = draggedIndex;
      }
      
      // Apply hysteresis: once we've committed to a drop index, 
      // require cursor to move past the CENTER of adjacent item to change
      if (currentDropIndex !== null && currentDropIndex !== draggedIndex) {
        // We have a committed drop position, apply stickiness
        if (newDropIndex !== currentDropIndex) {
          // Calculate the threshold - must cross center of the item at boundary
          let threshold;
          if (newDropIndex < currentDropIndex) {
            // Moving left - need to cross center of item at newDropIndex
            const checkIndex = currentDropIndex <= draggedIndex ? currentDropIndex : currentDropIndex + 1;
            threshold = itemCenters[checkIndex - 1];
            if (clientX >= threshold) {
              return; // Haven't crossed threshold, keep current
            }
          } else if (newDropIndex > currentDropIndex) {
            // Moving right - need to cross center of item after current drop position
            const checkIndex = currentDropIndex < draggedIndex ? currentDropIndex : currentDropIndex + 1;
            threshold = itemCenters[checkIndex];
            if (clientX <= threshold) {
              return; // Haven't crossed threshold, keep current
            }
          }
        }
      }
      
      // Update drop index
      if (newDropIndex === draggedIndex) {
        if (dropIndex !== null) {
          dragStateRef.current.currentDropIndex = null;
          setDropIndex(null);
        }
      } else if (newDropIndex !== dropIndex) {
        dragStateRef.current.currentDropIndex = newDropIndex;
        setDropIndex(newDropIndex);
      }
    };

    const handleMouseUp = () => {
      if (draggedIndex !== null && dropIndex !== null && dropIndex !== draggedIndex) {
        const reordered = [...projects];
        const [draggedItem] = reordered.splice(draggedIndex, 1);
        reordered.splice(dropIndex, 0, draggedItem);

        if (onReorderProjects) {
          onReorderProjects(reordered.map(p => p.id));
        }
      }
      
      dragStateRef.current.itemCenters = [];
      dragStateRef.current.currentDropIndex = null;
      setDraggedIndex(null);
      setDropIndex(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggedIndex, dropIndex, projects, onReorderProjects]);

  // Determine the visual class for each item during drag
  const getDragClass = (index) => {
    if (draggedIndex === null || dropIndex === null) return '';
    if (index === draggedIndex) return '';
    
    if (dropIndex > draggedIndex) {
      if (index > draggedIndex && index <= dropIndex) {
        return index === dropIndex
          ? 'header__project-wrapper--shift-left header__project-wrapper--drop-target-right'
          : 'header__project-wrapper--shift-left';
      }
    }
    
    if (dropIndex < draggedIndex) {
      if (index >= dropIndex && index < draggedIndex) {
        return index === dropIndex
          ? 'header__project-wrapper--shift-right header__project-wrapper--drop-target-left'
          : 'header__project-wrapper--shift-right';
      }
    }
    
    return '';
  };

  return (
    <header className="header">
      <div className="header__left">
        <h1 className="header__title">{t('app.title')}</h1>
        <CalendarSwitcher onManageCalendars={onManageCalendars} />
        <div className="header__year">
          <button 
            className="header__year-nav" 
            onClick={onPrevYear}
            title={t('calendar.previousYear')}
          >
            ‹
          </button>
          <span 
            className="header__year-label" 
            onClick={onGoToCurrentYear}
            title={t('calendar.goToCurrentYear')}
          >
            {year}
          </span>
          <button 
            className="header__year-nav" 
            onClick={onNextYear}
            title={t('calendar.nextYear')}
          >
            ›
          </button>
        </div>
      </div>

      <div 
        className={`header__projects ${draggedIndex !== null ? 'header__projects--dragging' : ''}`}
        ref={projectsContainerRef}
      >
        {projects.map((project, index) => {
          const isSelected = project.id === selectedProjectId;
          // Use translated name for system projects (Holidays)
          const displayName = project.isSystem ? t('holiday.holidaysName') : project.name;
          return (
          <div
            key={project.id}
            className={`header__project-wrapper ${draggedIndex === index ? 'header__project-wrapper--dragging' : ''} ${getDragClass(index)}`}
            onMouseDown={(e) => handleMouseDown(e, index)}
          >
            <button
              className={`header__project-pill ${!project.visible ? 'header__project-pill--hidden' : ''} ${isSelected ? 'header__project-pill--selected' : ''} ${project.isIncluded ? 'header__project-pill--shared' : ''}`}
              style={{ 
                '--project-color': project.color,
                backgroundColor: project.visible ? project.color : 'transparent',
                borderColor: project.color
              }}
              onClick={() => onSelectProject(project.id)}
              onContextMenu={(e) => {
                e.preventDefault();
                onEditProject(project);
              }}
              title={isSelected ? t('header.projectSelected', { name: displayName }) : t('header.projectNotSelected', { name: displayName })}
            >
              {project.isIncluded && <span className="header__project-shared-icon" title={t('sharing.sharedProject')}>↗</span>}
              <span 
                className={`header__project-dot ${isSelected ? 'header__project-dot--selected' : ''}`}
                style={{ backgroundColor: project.color }}
              />
              {displayName}
            </button>
            <button
              className="header__project-visibility"
              onClick={(e) => {
                e.stopPropagation();
                onToggleVisibility(project.id);
              }}
              title={project.visible ? t('header.hideProject') : t('header.showProject')}
            >
              {project.visible ? '\u{1F441}' : '\u{1F441}\u{FE0E}'}
            </button>
            <button
              className="header__project-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEditProject(project);
              }}
              title={t('header.editProject')}
            >
              ✎
            </button>
          </div>
          );
        })}
        <button 
          className="header__add-project"
          onClick={onAddProject}
          title={t('header.addProject')}
        >
          +
        </button>
      </div>

      <div className="header__right">
        <SyncIndicator />
        {isAuthenticated && onShowSharing && (
          <button 
            className="header__action header__action--sharing"
            onClick={onShowSharing}
            title={t('sharing.title')}
          >
            {t('sharing.title')}
            {pendingSharesCount > 0 && (
              <span className="header__badge">{pendingSharesCount}</span>
            )}
          </button>
        )}
        <LoginButton 
          onOpenSettings={onOpenSettings}
          onShowInfo={onShowInfo}
        />
      </div>
    </header>
  );
}

export default Header;
